import React, { useRef, useState } from 'react';
import { MainLayout } from '../../components/layout/MainLayout';
import { Upload, CheckCircle, AlertTriangle, FileText, X } from 'lucide-react';
import {
  uploadRetailerPricesCSV,
  UploadResult,
  ValidationError,
  ValidationFailure,
} from '../../api/services/configuratorUploadService';

type UploadState =
  | { status: 'idle' }
  | { status: 'uploading' }
  | { status: 'success'; result: UploadResult }
  | { status: 'validation'; failure: ValidationFailure }
  | { status: 'error'; message: string };

export default function ConfiguratorUploads() {
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [uploadState, setUploadState] = useState<UploadState>({ status: 'idle' });
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (selected: File | null) => {
    if (!selected) return;
    setFile(selected);
    setUploadState({ status: 'idle' });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped?.name.endsWith('.csv')) handleFileChange(dropped);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploadState({ status: 'uploading' });
    try {
      const result = await uploadRetailerPricesCSV(file);
      if ('isValidation' in result) {
        setUploadState({ status: 'validation', failure: result });
      } else {
        setUploadState({ status: 'success', result });
      }
    } catch (err) {
      setUploadState({ status: 'error', message: err instanceof Error ? err.message : 'Upload failed' });
    }
  };

  const reset = () => {
    setFile(null);
    setUploadState({ status: 'idle' });
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Configurator Uploads</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Upload a CSV file to sync retailer price data into{' '}
            <span className="font-mono">RetailersPricesMap</span>.
          </p>
        </div>

        {/* Expected format */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">Required CSV columns (in any order):</p>
          <code className="text-xs text-blue-700 dark:text-blue-400 break-all">
            RetailerFabricId, PriceId, Wholesale, Price
          </code>
          <ul className="mt-2 space-y-1 text-xs text-blue-700 dark:text-blue-400">
            <li><span className="font-medium">RetailerFabricId / PriceId</span> — GUID format (e.g. <span className="font-mono">xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx</span>)</li>
            <li><span className="font-medium">Wholesale</span> — required decimal (e.g. <span className="font-mono">12.50</span>)</li>
            <li><span className="font-medium">Price</span> — optional decimal, leave blank or empty for NULL</li>
          </ul>
          <p className="mt-2 text-xs text-blue-700 dark:text-blue-400">
            <span className="font-medium">RetailerPriceId</span> is auto-generated for each row. <span className="font-medium">Modified</span> is stamped with the import timestamp. Extra columns in the file (including those two) are ignored. All rows are inserted atomically — nothing is written if any row fails validation.
          </p>
        </div>

        {/* Drop zone */}
        <div
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
            dragging
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
          />
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileText size={24} className="text-blue-500 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-xs">{file.name}</span>
              <button
                onClick={(e) => { e.stopPropagation(); reset(); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex-shrink-0"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <Upload size={32} className="mx-auto text-gray-400" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Drop a CSV file here, or <span className="text-blue-500 font-medium">click to browse</span>
              </p>
              <p className="text-xs text-gray-400">.csv only — max 10 MB</p>
            </div>
          )}
        </div>

        {/* Upload button */}
        <button
          onClick={handleUpload}
          disabled={!file || uploadState.status === 'uploading'}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white font-medium rounded-lg transition-colors disabled:cursor-not-allowed"
        >
          {uploadState.status === 'uploading' ? (
            <>
              <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload size={16} />
              Upload &amp; Process
            </>
          )}
        </button>

        {/* Results */}
        {uploadState.status === 'success' && (
          <div className="flex items-start gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-green-800 dark:text-green-300">{uploadState.result.message}</p>
              <p className="text-sm text-green-700 dark:text-green-400 mt-0.5">
                {uploadState.result.rowsProcessed.toLocaleString()} new row{uploadState.result.rowsProcessed !== 1 ? 's' : ''} inserted into RetailersPricesMap.
              </p>
            </div>
          </div>
        )}

        {uploadState.status === 'error' && (
          <div className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <AlertTriangle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 dark:text-red-300">{uploadState.message}</p>
          </div>
        )}

        {uploadState.status === 'validation' && (
          <div className="space-y-3">
            <div className="flex items-start gap-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <AlertTriangle size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800 dark:text-yellow-300">{uploadState.failure.message}</p>
                <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-0.5">
                  Fix the errors below and re-upload the file.
                </p>
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Row</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Column</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Issue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {uploadState.failure.errors.map((err: ValidationError, i: number) => (
                    <tr key={i} className="bg-white dark:bg-gray-900">
                      <td className="px-4 py-2 text-gray-600 dark:text-gray-400 font-mono">{err.row}</td>
                      <td className="px-4 py-2 text-gray-800 dark:text-gray-200 font-mono">{err.column}</td>
                      <td className="px-4 py-2 text-red-600 dark:text-red-400">{err.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
